import React from "react";
import { motion } from "framer-motion";
import { FaRobot, FaCode, FaBrain, FaCloud, FaMicrochip, FaTools } from "react-icons/fa";

export function ServicesHeader() {
  const capabilities = [
    {
      icon: FaBrain,
      title: "AI Orchestration",
      description: "Semantic Kernel expertise for intelligent system design and deployment",
      color: "text-primary"
    },
    {
      icon: FaCloud,
      title: "Enterprise Integration",
      description: "Seamless Microsoft Stack implementation with AI capabilities",
      color: "text-cyan-500"
    },
    {
      icon: FaMicrochip,
      title: "AI Architecture",
      description: "RAG systems, vector search, and custom copilot development",
      color: "text-purple-500"
    },
    {
      icon: FaTools,
      title: "Team Enablement",
      description: "Technical leadership and AI implementation guidance",
      color: "text-green-500"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="relative overflow-hidden bg-gray-900 pt-16 pb-32">
        {/* Enhanced animated background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.2),transparent_50%),radial-gradient(circle_at_70%_60%,rgba(147,51,234,0.2),transparent_50%)]" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent opacity-20" />
        </div>

        {/* Dynamic orbs */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
          className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", delay: 1 }}
          className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-purple-500 blur-3xl"
        />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl font-bold tracking-tight sm:text-6xl leading-[1.3] pb-4"
            >
              <span className="gradient-heading pr-6">
                Enterprise AI Integration
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-lg leading-8 text-gray-300"
            >
              Accelerate your AI journey with expert guidance in Semantic Kernel, RAG systems, 
              and custom copilot development. We help teams build production-ready AI solutions 
              using the latest Microsoft technology stack.
            </motion.p>
          </div>

          {/* Enhanced feature cards grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mx-auto mt-16 grid max-w-lg grid-cols-1 gap-6 sm:mt-20 sm:max-w-4xl sm:grid-cols-2 lg:grid-cols-4"
          >
            {capabilities.map((capability, index) => (
              <div key={index} className="glass-card relative overflow-hidden group hover:scale-105 transition-transform duration-300">
                <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br from-primary to-purple-600 opacity-20 blur-2xl group-hover:opacity-30 transition-opacity" />
                <capability.icon className={`h-8 w-8 ${capability.color}`} />
                <h3 className="mt-4 text-xl font-semibold text-white">{capability.title}</h3>
                <p className="mt-2 text-gray-400">{capability.description}</p>
              </div>
            ))}
          </motion.div>

          {/* Call to action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="mt-10 flex items-center justify-center gap-4"
          >
            <a
              href="/contact?type=consulting"
              className="rounded-md bg-primary px-8 py-4 text-base font-semibold text-white shadow-sm hover:bg-primary/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Schedule a Consultation
            </a>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}